// bd-deepcontext-b4zw — request shaping, validation and job → form hydration,
// lifted out of ScheduledJobs.tsx's handleCreateJob / handleUpdateJob /
// startEdit. Pure, so the "empty collapses to undefined" rules are pinned by
// unit tests instead of only being reachable by mounting the page.
import { COUNTRY_REQUIRED_ERROR } from "../../constants/validation";
import type { SelectedDomainInfo } from "../SiteListModal";
import {
  buildDomainFilterUpdateFields,
  EMPTY_FORM_DATA,
  type ScheduledJobFormData,
} from "../scheduledJobFormDefaults";
import type {
  ScheduledJob,
  ScheduledJobCreateRequest,
  ScheduledJobUpdateRequest,
} from "./types";

export type JobFormMode = "create" | "edit" | "duplicate";

export interface JobFormError {
  message: string;
  /** Set when the message also belongs inline under a specific field. */
  field?: "countries";
}

// deepcontext-63k8.3 — the local-model pick is only ever sent for the user
// allowed to see it, so nobody else's save can touch the stored value. Null
// (Gemini) is sent explicitly so an update clears a previous pick.
const localModelField = (
  formData: ScheduledJobFormData,
  includeLocalModel: boolean,
): { localModelId?: string | null } =>
  includeLocalModel ? { localModelId: formData.localModelId } : {};

export const buildCreatePayload = (
  formData: ScheduledJobFormData,
  duplicateOfJobId?: string,
  includeLocalModel = false,
): ScheduledJobCreateRequest => ({
  name: formData.name,
  description: formData.description || undefined,
  urlsPerRun: formData.urlsPerRun,
  runsPerDay: formData.runsPerDay,
  countryCodes:
    formData.countryCodes.length > 0 ? formData.countryCodes : undefined,
  promptIds: formData.promptIds,
  selectedDomainInfos:
    formData.selectedDomainInfos.length > 0
      ? formData.selectedDomainInfos
      : undefined,
  domainExtensions:
    formData.domainExtensions.length > 0 ? formData.domainExtensions : undefined,
  excludeHomepages: formData.excludeHomepages,
  urlSourceMode: formData.urlSourceMode,
  countryDistributionMode: formData.countryDistributionMode,
  similaritySelectivity:
    formData.urlSourceMode === "classification"
      ? formData.similaritySelectivity
      : undefined,
  startDate: formData.startDate,
  endDate: formData.endDate,
  duplicateOfJobId,
  ...localModelField(formData, includeLocalModel),
});

export const buildUpdatePayload = (
  formData: ScheduledJobFormData,
  includeLocalModel = false,
): ScheduledJobUpdateRequest => ({
  name: formData.name,
  description: formData.description || undefined,
  urlsPerRun: formData.urlsPerRun,
  runsPerDay: formData.runsPerDay,
  countryCodes:
    formData.countryCodes.length > 0 ? formData.countryCodes : undefined,
  promptIds: formData.promptIds,
  // bd-deepcontext-1gak — always send both domain-filter arrays (even empty) so
  // clearing the selection actually reaches the backend.
  ...buildDomainFilterUpdateFields(formData),
  excludeHomepages: formData.excludeHomepages,
  urlSourceMode: formData.urlSourceMode,
  countryDistributionMode: formData.countryDistributionMode,
  similaritySelectivity:
    formData.urlSourceMode === "classification"
      ? formData.similaritySelectivity
      : undefined,
  startDate: formData.startDate || undefined,
  endDate: formData.endDate || undefined,
  autoSyncManaged: formData.autoSyncManaged,
  ...localModelField(formData, includeLocalModel),
});

export const validateJobForm = (
  formData: ScheduledJobFormData,
  mode: JobFormMode,
): JobFormError | null => {
  if (mode !== "edit" && !formData.name.trim()) {
    return { message: "Job name is required" };
  }

  if (formData.promptIds.length === 0) {
    return { message: "At least one prompt must be selected" };
  }

  if (mode !== "edit") {
    if (!formData.startDate || !formData.endDate) {
      return { message: "Start date and end date are required" };
    }
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      return { message: "Start date must be before end date" };
    }
  }

  if (formData.countryCodes.length === 0) {
    return { message: COUNTRY_REQUIRED_ERROR, field: "countries" };
  }

  return null;
};

export const jobToFormData = (job: ScheduledJob): ScheduledJobFormData => {
  // Domain infos are stored on the job but absent from the typed shape.
  const domainInfos: SelectedDomainInfo[] =
    (job as any).selectedDomainInfos || [];
  return {
    ...EMPTY_FORM_DATA,
    name: job.name,
    description: job.description || "",
    urlsPerRun: job.urlsPerRun,
    runsPerDay: job.runsPerDay,
    countryCodes: job.countryCodes || [],
    promptIds: job.promptIds || [],
    selectedDomainInfos: domainInfos,
    domainExtensions: job.domainExtensions || [],
    excludeHomepages:
      job.excludeHomepages !== undefined ? job.excludeHomepages : true,
    urlSourceMode: job.urlSourceMode || "default",
    countryDistributionMode: job.countryDistributionMode || "uniform",
    similaritySelectivity: job.similaritySelectivity ?? 0,
    startDate: job.startDate
      ? new Date(job.startDate).toISOString().slice(0, 10)
      : "",
    endDate: job.endDate ? new Date(job.endDate).toISOString().slice(0, 10) : "",
    // default to OFF (migration 014 flipped DEFAULT to 0)
    autoSyncManaged: job.autoSyncManaged === true,
    localModelId: job.localModelId ?? null,
  };
};

// deepcontext-c919.2 — same hydration as jobToFormData, but the dates are
// cleared: a duplicate starts with every field pre-filled from the source job
// except the active window, which must be set fresh.
export const jobToDuplicateFormData = (
  job: ScheduledJob,
): ScheduledJobFormData => ({
  ...jobToFormData(job),
  startDate: "",
  endDate: "",
});

// The pool-estimate request mirrors the create/update payload's filter fields.
export const buildPoolEstimateParams = (
  formData: ScheduledJobFormData,
): Record<string, unknown> => ({
  urlSourceMode: formData.urlSourceMode,
  countryCodes:
    formData.countryCodes.length > 0 ? formData.countryCodes : undefined,
  promptIds: formData.promptIds,
  similaritySelectivity:
    formData.urlSourceMode === "classification"
      ? formData.similaritySelectivity
      : undefined,
  countryDistributionMode: formData.countryDistributionMode,
  selectedDomainInfos:
    formData.selectedDomainInfos.length > 0
      ? formData.selectedDomainInfos
      : undefined,
  domainExtensions:
    formData.domainExtensions.length > 0 ? formData.domainExtensions : undefined,
  excludeHomepages: formData.excludeHomepages,
});
